import React, { useState } from "react";

import { CheckCircleRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { Alert } from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { ComponentPrice } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { Subscription } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { Currency } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { toUUID } from "../../../../helpers/s12-helpers";
import { UrlParamTypes } from "../../../../types/org-display";
import ComponentPriceInfo from "../../../shared/component-price-info";
import { updateSubscriptionComponentPrice } from "api/endpoints/components";
import { setEnterpriseMetadata } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import { ChangeBillingDateWrapperProps } from "types/org-display";

export type StepItem = {
  step: number;
  label: string;
  description: React.ReactElement;
  alternativeLabel: string;
  action: (step: number) => Promise<boolean>;
};

interface ChangeComponentPricePointToEnterpriseProps {
  subscription: Subscription;
  onComplete: () => void;
  onStartProcess: () => void;
  chargifyId: string;
  chargifySite: string;
  currPrice: ComponentPrice;
  newPrice: ComponentPrice;
  currency: Currency;
  quantity: number;
  isFreeOrg: boolean;
  subActive: boolean;
}

type Props = ChangeBillingDateWrapperProps &
  ChangeComponentPricePointToEnterpriseProps;

const ChangeComponentPricePointToEnterprise = ({
  subscription,
  onComplete,
  onStartProcess,
  currPrice,
  newPrice,
  quantity,
  isFreeOrg,
  subActive,
}: Props) => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const classes = mainStyles();
  const [activeStep, setActiveStep] = useState(-1);
  const [isSubmit, setIsSubmit] = useState(false);
  const [processCompleted, setProcessCompleted] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const subId = subscription.id;

  const submitPricePointChange = async (
    activeStep: number
  ): Promise<boolean> => {
    const componentPricePointId = newPrice?.id;
    const updated = await updateSubscriptionComponentPrice(
      {
        subId: toUUID(subId),
        componentPriceId: componentPricePointId,
        organizationId: toUUID(roleId),
      },
      callOptionsHeaders(regionCode, roleId)
    );
    if (updated) {
      setActiveStep(activeStep + 1);
      return new Promise((resolve) => resolve(true));
    } else {
      return new Promise((resolve) => resolve(false));
    }
  };

  const submitCustomerFieldChange = async (
    activeStep: number
  ): Promise<boolean> => {
    const result = await setEnterpriseMetadata(
      {
        subId: toUUID(subId),
      },
      callOptionsHeaders(regionCode, roleId)
    );
    if (result) {
      setActiveStep(activeStep + 1);
      return new Promise((resolve) => resolve(true));
    } else {
      return new Promise((resolve) => resolve(false));
    }
  };

  const PREMIUM_TO_ENTERPRISE_STEPS: StepItem[] = [
    {
      step: 0,
      label: "Step 1",
      description: (
        <>
          Update seat price from <b>Premium</b> to <b>Enterprise</b> in Chargify
        </>
      ),
      alternativeLabel: "",
      action: submitPricePointChange,
    },
    {
      step: 1,
      label: "Step 2",
      description: (
        <>
          Update <em>custom_field</em> in Chargify, set <b>Plan Type</b> to{" "}
          <b>Enterprise</b>
        </>
      ),
      alternativeLabel: "",
      action: submitCustomerFieldChange,
    },
  ];

  const processSteps = async (steps: StepItem[]) => {
    let i = 0;
    const actions: Array<Promise<boolean>> = [];
    while (i < steps.length) {
      const action = await steps[i].action(i);
      if (action) {
        i++;
        actions.push(new Promise((resolve) => resolve(true)));
      } else {
        actions.push(new Promise((resolve) => resolve(false)));
        break;
      }
    }
    return actions;
  };

  const startProcess = async () => {
    setIsSubmit(true);
    onStartProcess();
    setActiveStep(0);
    const defaultError = new Error(
      "please contact BSA Team at #business-systems slack channel"
    );
    const actions: Array<Promise<boolean>> = await processSteps(steps);
    Promise.all(actions)
      .then(async (res) => {
        if (res.some((e) => e !== true)) {
          setError(defaultError);
        } else {
          setProcessCompleted(true);
          return true;
        }
      })
      .catch((error) => {
        setError(error || defaultError);
      });
  };

  const steps = PREMIUM_TO_ENTERPRISE_STEPS;
  const [salesforceConfirmed, setSalesforceConfirmed] =
    useState<boolean>(false);
  const [seatPriceConfirmed, setSeatPriceConfirmed] = useState<boolean>(false);

  const confirmationCheckboxes = () => {
    return (
      <div
        className={clsx(classes.spaceTopSmall, classes.spaceBottomSmall)}
        data-testid="price-summary"
      >
        <Grid
          justifyContent="center"
          className={clsx(classes.spaceTopSmall, classes.spaceBottomSmall)}
        >
          <b>Current price:</b>
          <ComponentPriceInfo componentPrice={currPrice} unitLabel="seat" />
        </Grid>
        <Grid
          justifyContent="center"
          className={clsx(classes.spaceTopSmall, classes.spaceBottomSmall)}
        >
          <b>New price:</b>{" "}
          <ComponentPriceInfo componentPrice={newPrice} unitLabel="seat" />
        </Grid>
        <Grid>
          <Box component="span" fontWeight="fontWeightBold">
            Quantity:
          </Box>{" "}
          {quantity}
        </Grid>
        <FormControlLabel
          className={clsx(classes.spaceTopLarge)}
          label="New price and quantity is correct"
          data-testid="price-and-quantity-confirmation"
          control={
            <Checkbox
              checked={seatPriceConfirmed}
              disabled={isSubmit}
              onChange={() => setSeatPriceConfirmed(!seatPriceConfirmed)}
            />
          }
        />
        <FormControlLabel
          label="(optional) Activated enterprise contract in Salesforce exists"
          data-testid="salesforce-confirmation"
          control={
            <Checkbox
              disabled={isSubmit}
              checked={salesforceConfirmed}
              onChange={() => setSalesforceConfirmed(!salesforceConfirmed)}
            />
          }
        />
      </div>
    );
  };

  const confirmationContainer = () => {
    return (
      <Grid
        container
        justifyContent="center"
        className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
      >
        <Typography variant="h6" align="left" color="textPrimary" gutterBottom>
          Please review the details
        </Typography>
        <Alert severity="warning" variant="filled">
          Please change the other seat (full or lite) to Enterprise
        </Alert>
        {confirmationCheckboxes()}
      </Grid>
    );
  };

  const compeletedMessage = () => {
    return (
      <Grid style={{ textAlign: "center" }}>
        <CheckCircleRounded color="success" fontSize="large" />
        <Typography variant="h6">Processing completed</Typography>
        <Button
          className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => onComplete()}
          data-testid="complete-btn"
        >
          Close
        </Button>
      </Grid>
    );
  };

  const stepperContainer = () => {
    return (
      <>
        <Button
          className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => startProcess()}
          data-testid="change-component-price-start-btn"
          disabled={!salesforceConfirmed || !seatPriceConfirmed || isSubmit}
        >
          Start processing
        </Button>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          className={clsx(classes.spaceTopLarge)}
          data-testid="process-steppers"
        >
          {steps.map((stepItem) => (
            <Step key={stepItem.step} className={clsx(classes.spaceTopLarge)}>
              <StepLabel>
                <Typography variant="body2">{stepItem.label}</Typography>
                {stepItem.description}
              </StepLabel>

              {activeStep === stepItem.step && isSubmit && !error && (
                <LinearProgress />
              )}
            </Step>
          ))}
        </Stepper>
        {error && (
          <Alert
            variant="filled"
            severity="error"
            className={clsx(classes.spaceTopLarge)}
          >
            Error occurred: {error?.message}
          </Alert>
        )}
      </>
    );
  };

  if (!subscription || isFreeOrg || !subActive) {
    return (
      <Typography variant="h6">
        Invalid subscription - cannot process component change from Premium to
        Enterprise for this subscription.
      </Typography>
    );
  }

  return (
    <>
      {!processCompleted ? (
        <>
          {confirmationContainer()}
          {seatPriceConfirmed && salesforceConfirmed && stepperContainer()}
        </>
      ) : (
        compeletedMessage()
      )}
    </>
  );
};

export default ChangeComponentPricePointToEnterprise;
